/**
 * Verf-PDP — "Jouw klus" (Claude Design variant 4e).
 *
 * Klapt de ruimtefilter en de afwerkingslijst in tot één kaart met het gekozen advies.
 * De keuzes zelf blijven de bestaande radio's: dit script toont, verbergt en vat samen,
 * het kiest niets. De variant-picker morpht zichzelf bij elke variantwissel, dus na elke
 * morph worden samenvatting en vraagteksten opnieuw gezet.
 *
 * A/B-test (SHO-11108, docs/ab-verf-klus.md): de kaart staat in beide armen in de HTML.
 * Dit script kiest de arm en zet hem als [data-klus-arm]; alleen treatment krijgt de kaart.
 * In beide armen meet het dezelfde keuzes (ruimte, afwerking) en zet het de ruimte en of de
 * afwerking ons advies was als verborgen eigenschappen op de orderregel.
 */

import { exposeWhenVisible, isForced, setTreatmentShare, track, variantOf } from '@theme/hds-experiments';

const DURATION = 320;
const EASE = 'cubic-bezier(.2, .8, .2, 1)';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Gekozen open/dicht blijft staan als de sectie morpht en het element opnieuw rendert. */
let userOpen = null;

/**
 * Ruimtes in de taal van de klant (design 4e), in deze volgorde. `app` is de waarde uit
 * kenmerk.toepassing; die blijft het filter sturen. "Badkamer of keuken" bestaat niet in de
 * data: het is "Muur en plafond binnen" met alleen de afwerkingen die (af)wasbaar of
 * schrobvast zijn volgens het afwerking-metaobject (wasbestendigheid).
 * De labels komen uit de locale (template .paint-klus__labels, [data-app]).
 */
const WET = 'wet';
const ROOMS = [
  { app: 'Muur en plafond binnen' },
  { key: WET, app: 'Muur en plafond binnen' },
  { app: 'Hout en metaal binnen' },
  { app: 'Vloer en trap binnen' },
  { app: 'Muur buiten' },
  { app: 'Hout en metaal buiten' },
];
const WET_APP = ROOMS.find((r) => r.key === WET).app;
const WASHABLE = /afwasbaar|schrobvast|wasbaar/i;
const NOT_WASHABLE = /beperkt|niet/i;

/** Gekozen klantruimte die niet uit de radio's af te leiden is (alleen "Badkamer of keuken"). */
let roomKey = null;

function card() {
  return document.querySelector('.paint-klus');
}

function parts(klus) {
  const parent = klus.parentElement;
  const filter = parent?.querySelector(':scope > paint-application-filter');
  const finishes = parent?.querySelector(':scope > variant-picker .variant-option--afwerking-cards');
  return { filter, finishes };
}

function checkedRoom(filter) {
  return filter?.querySelector('input[type="radio"]:checked')?.value?.trim() || '';
}

function checkedFinish(finishes) {
  const input = finishes?.querySelector('input[type="radio"]:checked');
  return input ? input.closest('label') : null;
}

function text(el) {
  return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
}

/** Klantlabel uit de locale; zonder vertaling de waarde uit de data. */
function labelFor(key) {
  const labels = card()?.querySelector('.paint-klus__labels')?.content;
  const found = [...(labels?.querySelectorAll('[data-app]') || [])].find((el) => el.dataset.app === key);
  return found?.textContent.trim() || key;
}

function roomLabel(app) {
  return labelFor(roomKey === WET && app === WET_APP ? WET : app);
}

function isWashable(label) {
  return [...label.querySelectorAll('.afwerking-card__tag')].some((tag) => {
    const value = text(tag);
    return WASHABLE.test(value) && !NOT_WASHABLE.test(value);
  });
}

function appliesTo(label, app) {
  const raw = label.dataset.application || '';
  try {
    const parsed = JSON.parse(raw);
    return (Array.isArray(parsed) ? parsed : [parsed]).includes(app);
  } catch {
    return raw.split(',').map((a) => a.trim()).includes(app);
  }
}

/** Zet de klantlabels, de volgorde en de extra keuze "Badkamer of keuken" in de filter. */
function setupRooms(klus) {
  const { filter, finishes } = parts(klus);
  if (!filter || filter.dataset.klusRooms === 'true') return;
  filter.dataset.klusRooms = 'true';

  const labels = [...filter.querySelectorAll('label.variant-option__button-label')];
  labels.forEach((label) => {
    const app = label.querySelector('input')?.value;
    const index = ROOMS.findIndex((r) => !r.key && r.app === app);
    const textEl = label.querySelector('.variant-option__button-label__text');
    if (index === -1 || !textEl) return;
    textEl.textContent = labelFor(app);
    label.style.order = String(index);
  });

  // Alleen aanbieden als er voor muren binnen echt een (af)wasbare afwerking is.
  const base = labels.find((l) => l.querySelector('input')?.value === WET_APP);
  const hasWet = [...(finishes?.querySelectorAll('label.variant-option__button-label') || [])].some(
    (l) => appliesTo(l, WET_APP) && isWashable(l)
  );
  if (!base || !hasWet) return;

  const wet = document.createElement('label');
  wet.className = 'variant-option__button-label paint-klus__room';
  wet.dataset.klusRoom = WET;
  wet.style.order = String(ROOMS.findIndex((r) => r.key === WET));
  wet.innerHTML = '<input type="radio" class="paint-klus__room-input"><span class="variant-option__button-label__text"></span>';
  wet.querySelector('input').name = `paint-klus-room-${Math.random().toString(36).slice(2)}`;
  wet.querySelector('.variant-option__button-label__text').textContent = labelFor(WET);
  base.after(wet);

  wet.addEventListener('click', (event) => {
    event.preventDefault();
    roomKey = WET;
    klus.dataset.room = WET;
    const input = base.querySelector('input');
    // Staat muren al aan, dan komt er geen filter-event: zelf bijwerken.
    if (input.checked) applyState(klus);
    else input.click();
  });
  labels.forEach((label) => {
    label.addEventListener('click', (event) => {
      // Alleen een klik van de klant: de keuze "Badkamer of keuken" klikt zelf de muren-radio aan.
      if (!event.isTrusted) return;
      roomKey = null;
      delete klus.dataset.room;
      clearWet(klus);
    });
  });
}

function clearWet(klus) {
  parts(klus).finishes?.querySelectorAll('.paint-klus-dry').forEach((l) => l.classList.remove('paint-klus-dry'));
}

/** Badkamer of keuken: verberg niet-wasbare afwerkingen en kies zo nodig de eerste wasbare. */
function applyWet(klus) {
  const { finishes } = parts(klus);
  if (roomKey !== WET || !finishes) return;
  const labels = [...finishes.querySelectorAll('label.variant-option__button-label')];
  labels.forEach((label) => label.classList.toggle('paint-klus-dry', !isWashable(label)));
  const checked = checkedFinish(finishes);
  if (checked && !checked.classList.contains('paint-klus-dry')) return;
  const first = labels.find(
    (l) =>
      !l.classList.contains('paint-klus-dry') &&
      !l.classList.contains('variant-option--filtered-out') &&
      !l.classList.contains('variant-option--hidden-by-liquid') &&
      !l.querySelector('input')?.disabled
  );
  first?.querySelector('input')?.click();
}

function syncSummary(klus) {
  const { filter, finishes } = parts(klus);
  const room = checkedRoom(filter);
  const label = checkedFinish(finishes);

  const forEl = klus.querySelector('.paint-klus__for');
  const template = forEl?.dataset.template || '';
  if (forEl && room) forEl.textContent = template.replace('%room%', roomLabel(room).toLowerCase());

  const nameEl = klus.querySelector('.paint-klus__name');
  const specsEl = klus.querySelector('.paint-klus__specs');
  const descEl = klus.querySelector('.paint-klus__desc');
  if (!label) return false;

  nameEl.textContent = text(label.querySelector('.variant-option__button-label__text'));
  specsEl.textContent = [...label.querySelectorAll('.afwerking-card__tag')]
    .map(text)
    .filter(Boolean)
    .join(' · ');
  descEl.textContent = text(label.querySelector('.afwerking-card__desc'));
  return true;
}

/** Binnen de kaart stellen de stappen een vraag in plaats van een kopje. */
function relabel(klus) {
  const { filter, finishes } = parts(klus);
  const labels = klus.querySelector('.paint-klus__labels')?.content;
  if (!labels) return;
  const set = (scope, key) => {
    const legend = scope?.querySelector('.variant-option__legend-label');
    const value = labels.querySelector(`[data-key="${key}"]`)?.textContent.trim();
    if (!legend || !value) return;
    const node = [...legend.childNodes].reverse().find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
    if (node) node.textContent = value;
    else legend.append(value);
  };
  set(filter, 'room');
  set(finishes, 'finish');
}

function setExpanded(klus, open) {
  const head = klus.querySelector('.paint-klus__head');
  head?.setAttribute('aria-expanded', String(open));
  klus.dataset.head = open ? 'open' : 'closed';
}

/**
 * Hoeveel de rest van de kolom nog verspringt als de ingeklapte delen (hoogte 0) echt
 * uit beeld gaan: hun binnenmarges, tussenruimtes en randen. Dat verschil animeren we mee als marge
 * onder de kop, zodat alles eronder in één vloeiende beweging op zijn plek komt.
 */
function settleOffset(klus, targets) {
  const picker = klus.parentElement.querySelector(':scope > variant-picker');
  if (!picker) return 0;
  const state = klus.dataset.state;
  klus.dataset.state = 'closing';
  targets.forEach((el) => {
    el.style.height = '0px';
  });
  const zero = picker.getBoundingClientRect().bottom;
  targets.forEach((el) => {
    el.style.height = '';
  });
  klus.dataset.state = 'closed';
  const closed = picker.getBoundingClientRect().bottom;
  klus.dataset.state = state;
  return closed - zero;
}

function animateParts(klus, open) {
  const { filter, finishes } = parts(klus);
  const targets = [filter, finishes].filter(Boolean);
  if (reducedMotion.matches) return Promise.resolve();

  const offset = settleOffset(klus, targets);
  const settle = klus.animate(
    open
      ? [{ marginBottom: `${offset}px` }, { marginBottom: '0px' }]
      : [{ marginBottom: '0px' }, { marginBottom: `${offset}px` }],
    { duration: DURATION, easing: EASE, fill: 'both' }
  );

  return Promise.all([
    settle.finished.catch(() => {}),
    ...targets.map((el) => {
      const height = el.getBoundingClientRect().height;
      // Binnenmarges staan met !important (live-CSS) en animeren niet; settleOffset vangt ze op.
      // Kleiner dan marge + rand kan de hoogte niet: eindig daar precies, anders stokt hij vóór het eind.
      const style = getComputedStyle(el);
      const floor = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
        .reduce((sum, prop) => sum + (parseFloat(style[prop]) || 0), 0);
      const shut = { height: `${floor}px`, opacity: 0 };
      const full = { height: `${height}px`, opacity: 1 };
      // Inhoud vervaagt sneller dan de hoogte (mockup: .22s tegen .32s), zodat er bij
      // dichtklappen geen half verdwenen kopjes over elkaar schuiven.
      const frames = open ? [shut, full] : [full, { opacity: 0, offset: 0.6 }, shut];
      el.style.overflow = 'hidden';
      const anim = el.animate(frames, {
        duration: DURATION,
        easing: EASE,
        fill: 'both',
      });
      return anim.finished
        .catch(() => {})
        .then(() => {
          el.style.overflow = '';
          anim.cancel();
        });
    }),
  ]).then(() => settle.cancel());
}

/* ---------------------------------------------------------------- A/B-test en meting */

/** @type {'control' | 'treatment' | null} arm van deze bezoeker, per paginaweergave vast */
let arm = null;

function inExperiment(klus) {
  return klus.dataset.klusModus === 'experiment' && Boolean(klus.dataset.klusExp);
}

/** Uit = geen kaart (rendert dan niet eens); Altijd = iedereen treatment, zonder meting van een arm. */
function chooseArm(klus) {
  try {
    if (klus.dataset.klusModus === 'aan') return 'treatment';
    if (!inExperiment(klus)) return 'control';
    setTreatmentShare(klus.dataset.klusExp, klus.dataset.klusAandeel || 20);
    return variantOf(klus.dataset.klusExp);
  } catch {
    return 'control'; // meten mag winkelen nooit breken: dan de pagina zoals hij was
  }
}

function visibleFinishes(finishes) {
  return [...(finishes?.querySelectorAll('label.variant-option__button-label') || [])].filter(
    (l) =>
      !l.classList.contains('paint-klus-dry') &&
      !l.classList.contains('variant-option--filtered-out') &&
      !l.classList.contains('variant-option--hidden-by-liquid')
  );
}

/** Ruimte zoals de klant hem koos: de waarde uit de data, of "Badkamer of keuken". */
function roomCode(app) {
  return roomKey === WET && app === WET_APP ? 'Badkamer of keuken' : app;
}

/** De keuze zoals hij nu staat. `position` 1 = de eerste afwerking voor die ruimte: ons advies. */
function snapshot(klus) {
  const { filter, finishes } = parts(klus);
  const label = checkedFinish(finishes);
  return {
    room: roomCode(checkedRoom(filter)),
    finish: text(label?.querySelector('.variant-option__button-label__text')),
    position: label ? visibleFinishes(finishes).indexOf(label) + 1 : 0,
  };
}

/** Eén event naar de pixel; buiten een A/B-test (Altijd) zonder experiment-velden, zoals de vergelijker. */
function send(klus, name, extra) {
  const experiment = inExperiment(klus);
  track(name, {
    experiment_id: experiment ? klus.dataset.klusExp : '',
    variant: experiment ? arm : '',
    product_id: klus.dataset.productId || '',
    brand: klus.dataset.brand || '',
    ...extra,
  });
}

/**
 * Meetellen zodra het verschil in beeld komt: in treatment de kaart, in control de ruimtefilter
 * (die staat op dezelfde plek). `entry` scheidt bezoekers die via een link met ?variant=
 * (Shopping) binnenkomen en dus al een afwerking gekozen hebben.
 */
function exposeOnce(klus) {
  if (!inExperiment(klus)) return;
  try {
    const anchor = arm === 'treatment' ? klus : parts(klus).filter;
    const start = snapshot(klus);
    exposeWhenVisible(klus.dataset.klusExp, anchor || klus, {
      placement: arm === 'treatment' ? 'klus' : 'filter',
      product_id: klus.dataset.productId || '',
      brand: klus.dataset.brand || '',
      start_room: start.room,
      start_finish: start.finish,
      entry: new URLSearchParams(window.location.search).has('variant') ? 'variant_url' : 'direct',
    });
  } catch {
    // zonder blootstelling telt deze bezoeker niet mee; winkelen gaat door
  }
}

/** Open/dicht van de kaart (alleen treatment). */
let openedAt = 0;
let openSnap = null;
let opens = 0;
let everOpened = false;

function trackOpen(klus) {
  opens += 1;
  everOpened = true;
  openedAt = performance.now();
  openSnap = snapshot(klus);
  send(klus, 'hds:paint_klus_open', { trigger: 'head', nth_open: opens, room: openSnap.room, finish: openSnap.finish });
}

function trackClose(klus, trigger) {
  const now = snapshot(klus);
  send(klus, 'hds:paint_klus_close', {
    trigger,
    ms_open: openedAt ? Math.round(performance.now() - openedAt) : 0,
    changed_room: Boolean(openSnap && openSnap.room !== now.room),
    changed_finish: Boolean(openSnap && openSnap.finish !== now.finish),
    room: now.room,
    finish: now.finish,
  });
  openedAt = 0;
  openSnap = null;
}

/**
 * Keuzes van de klant, in beide armen. Alleen echte klikken (isTrusted): het filter kiest zelf
 * een afwerking als de oude niet bij de ruimte past, en dat is geen keuze van de klant — die
 * wissel komt mee als `finish_auto_switched`. De keuze is pas te lezen nadat filter en
 * picker gereageerd hebben, vandaar de korte vertraging.
 */
let lastLabel = null;
let lastAt = 0;

function onChoiceClick(event) {
  if (!event.isTrusted) return;
  const klus = card();
  if (!klus || !arm || !(event.target instanceof Element)) return;
  const label = event.target.closest('label.variant-option__button-label');
  if (!label) return;
  // Een klik op een label komt nog eens binnen als klik op zijn input.
  if (label === lastLabel && event.timeStamp - lastAt < 100) return;
  lastLabel = label;
  lastAt = event.timeStamp;

  const { filter, finishes } = parts(klus);
  const isRoom = filter?.contains(label);
  const isFinish = finishes?.contains(label);
  if (!isRoom && !isFinish) return;

  const before = snapshot(klus);
  setTimeout(() => {
    const after = snapshot(klus);
    if (isRoom) {
      if (after.room === before.room) return;
      send(klus, 'hds:paint_room_select', {
        room: after.room,
        from_room: before.room,
        finish: after.finish,
        finish_auto_switched: after.finish !== before.finish,
        finishes_visible: visibleFinishes(parts(klus).finishes).length,
      });
    } else {
      if (after.finish === before.finish) return;
      send(klus, 'hds:paint_finish_select', {
        finish: after.finish,
        from_finish: before.finish,
        room: after.room,
        position: after.position,
        is_advice: after.position === 1,
      });
    }
    syncCartProps(klus);
  }, 200);
}

/**
 * Verborgen eigenschappen op de orderregel (underscore: de klant ziet ze niet). Daarmee zijn
 * retouren en klantcontact per ruimte en afwerking te volgen, ook zonder cookie-toestemming.
 * Alleen tijdens de A/B-test en niet bij een afgedwongen arm (preview).
 */
const CART_PROPS = ['_ruimte', '_afwerking_advies', '_klus_geopend'];

function productForm(klus) {
  const section = klus.closest('.shopify-section') || document;
  return section.querySelector('product-form-component form') || document.querySelector('product-form-component form');
}

function syncCartProps(klus) {
  try {
    if (!inExperiment(klus) || isForced(klus.dataset.klusExp)) return;
    const form = productForm(klus);
    if (!form) return;
    const now = snapshot(klus);
    const values = {
      _ruimte: now.room,
      _afwerking_advies: now.position === 1 ? 'ja' : 'nee',
      _klus_geopend: arm === 'treatment' ? (everOpened ? 'ja' : 'nee') : '',
    };
    for (const key of CART_PROPS) {
      const name = `properties[${key}]`;
      let input = form.querySelector(`input[name="${name}"]`);
      if (!values[key]) {
        input?.remove();
        continue;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.append(input);
      }
      input.value = values[key];
    }
  } catch {
    // zonder eigenschappen gaat de bestelling gewoon door
  }
}

/** Vlak vóór het in de mand leggen nog eens: de buy-buttons kunnen intussen gemorpht zijn. */
function syncBeforeAdd(event) {
  const klus = card();
  if (!klus || !arm || !(event.target instanceof Element)) return;
  if (event.type === 'submit' || event.target.closest('product-form-component, add-to-cart-component, sticky-add-to-cart')) {
    syncCartProps(klus);
  }
}

document.addEventListener('click', onChoiceClick, true);
document.addEventListener('click', syncBeforeAdd, true);
document.addEventListener('submit', syncBeforeAdd, true);

let busy = Promise.resolve();

function toggle(klus, open) {
  userOpen = open;
  busy = busy.then(async () => {
    if (open) {
      setExpanded(klus, true);
      klus.dataset.state = 'open';
      await animateParts(klus, true);
    } else {
      setExpanded(klus, false);
      klus.dataset.state = 'closing';
      await animateParts(klus, false);
      klus.dataset.state = 'closed';
    }
  });
  return busy;
}

/** Zonder gekozen afwerking valt er niets samen te vatten: dan staat de kaart open. */
function applyState(klus) {
  if (arm !== 'treatment') {
    syncCartProps(klus);
    return;
  }
  // Eerst de afwerkingen voor "Badkamer of keuken" verbergen: het advies telt alleen wat zichtbaar is.
  applyWet(klus);
  syncCartProps(klus);
  const hasChoice = syncSummary(klus);
  relabel(klus);
  const open = userOpen ?? !hasChoice;
  if (klus.dataset.state === 'closing') return;
  klus.dataset.state = open ? 'open' : 'closed';
  setExpanded(klus, open);
}

function init() {
  const klus = card();
  if (!klus || klus.dataset.ready === 'true') return;
  klus.dataset.ready = 'true';

  // Meestal heeft het inline script in de block de arm al gezet; het script blijft de bron,
  // dus bij een (theoretisch) verschil wint variantOf().
  arm = arm ?? chooseArm(klus);
  if (klus.dataset.klusArm !== arm) klus.dataset.klusArm = arm;
  exposeOnce(klus);
  if (arm !== 'treatment') {
    syncCartProps(klus);
    return;
  }

  // Laat de kaart aansluiten op de echte afstand tussen de blokken.
  const gap = parseFloat(getComputedStyle(klus.parentElement).rowGap);
  if (!Number.isNaN(gap)) klus.parentElement.style.setProperty('--pk-gap', `${gap}px`);

  setupRooms(klus);
  klus.querySelector('.paint-klus__head')?.addEventListener('click', (event) => {
    const open = klus.dataset.head !== 'open';
    if (open) trackOpen(klus);
    else trackClose(klus, event.target instanceof Element && event.target.closest('.paint-klus__done') ? 'klaar' : 'head');
    toggle(klus, open);
  });

  applyState(klus);
}

function resync() {
  const klus = card();
  if (!klus) return;
  if (klus.dataset.ready !== 'true') {
    init();
    return;
  }
  // Na een morph kan het element zijn attributen terug hebben gekregen.
  if (arm) klus.dataset.klusArm = arm;
  requestAnimationFrame(() => applyState(klus));
}

document.addEventListener('paint-filter:changed', () => setTimeout(resync, 0));
document.addEventListener('hds:variant-picker-morphed', resync);
document.addEventListener('hds:main-morphed', resync);
document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.type === 'radio') resync();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
